import { Ability, RawRuleOf } from '@casl/ability';
import { createContextualCan } from '@casl/react';
import getConfig from 'next/config';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Actions, Subjects } from '../../../server/abilities/UserAbilityEnums';
import {
  Role,
  SubscriptionPlanTier,
  TeamRole,
} from '../../../server/graphql/__generated.types';
import { useUserSubscription } from '../../components/Account/operation.generated';
import { checkSubscriptionPause } from '../../components/Account/UserSubscriptionCard/churnkey';
import createSigoutProps from '../../lib/createSignoutProps';
import isBrowser from '../../util/isBrowser';
import { setUserContext as setUserContextForSentry } from '../../util/sentry';
import {
  UserContextQueryHookResult,
  useUserContextQuery,
} from './operation.generated';
import { Environment } from '../../../server/util/env';

export { Actions };
export { Subjects };

type ContextState = UserContextQueryHookResult;

export type UserAbility = Ability<[Actions, Subjects]>;

const {
  AUTH0_MAKER_ROLE_ID,
  AUTH0_CREATIVE_ROLE_ID,
  AUTH0_PRODUCER_ROLE_ID,
  AUTH0_BETA_ROLE_ID,
  AUTH0_DESIGNER_BETA_ROLE_ID,
  AUTH0_TEAM_ENTERPRISE_ROLE_ID,
  _ENV,
} = getConfig().publicRuntimeConfig;
const environment = (_ENV as Environment | undefined) || 'dev';

/**
 * Abilities Context
 */
export const unpackAbility = (ability: string) => {
  try {
    return JSON.parse(ability).A as RawRuleOf<UserAbility>[];
  } catch (_error) {
    throw new Error(
      'Error unpacking `UserAbility` within `UserAbilitiesContext`'
    );
  }
};
export const UserAbilitiesContext = createContext<UserAbility>(new Ability());
UserAbilitiesContext.displayName = 'UserAbilitiesContext';

export const Can = createContextualCan<UserAbility>(
  UserAbilitiesContext.Consumer
);

export function useUserAbilitiesContext() {
  return useContext(UserAbilitiesContext);
}

/**
 * User Context
 */
export const UserContext = createContext<ContextState>(
  // @ts-ignore No default value, force use under Provider
  null
);
UserContext.displayName = 'UserContext';

export function useUserContext(): ContextState {
  const userContext: ContextState | null = useContext(UserContext);
  if (!userContext)
    throw new Error(
      `Attempting to use UserContext outside of the <UserProvider />`
    );
  return userContext;
}

/**
 * @todo [team_expansion] deprecate this
 */
export function useUserTeam() {
  const { data } = useUserContext();
  return data?.user.team;
}

export function useUserTeamRole() {
  const { data } = useUserContext();
  return data?.user.team?.viewersRoleOnTeam;
}

/**
 * @description delineates whether a user is a a traditional team user (multi-seat)
 * single-seat users would return false
 * @deprecated Use RBAC user abilities
 */
export function useIsTeamMultiSeat() {
  const { data } = useUserContext();
  return Boolean(data?.user.subscription?.plan?.isMultiSeat);
}

/**
 * @description check is the current user is a managed user
 */
export function useIsManagedUser() {
  const { data } = useUserContext();
  return Boolean(data?.user.subscription?.plan?.managed);
}

export function useIsUserTeamAdminOrOwner() {
  const teamData = useUserTeam();
  if (!teamData) {
    return false;
  }
  return isTeamAdminOrOwner(teamData);
}

/**
 * @note this utility checks if enterprise user on new system
 */
export function isEnterpriseUser(planName?: string) {
  if (!planName) {
    return false;
  }
  return planName === 'Enterprise';
}

const isTeamAdminOrOwner = (teamData: any) => {
  return (
    teamData?.viewersRoleOnTeam === TeamRole.Admin ||
    teamData?.viewersRoleOnTeam === TeamRole.Owner
  );
};

export function hasTrialRole(roles: Role[]) {
  return roles.some(role => role.id === AUTH0_BETA_ROLE_ID);
}

/**
 * @todo apply a more stable tier identity in the future when available
 * @note we are not fuzzy inspecting display name here due to designer trial and business trial
 */
export function useIsTrialUser() {
  const { data } = useUserContext();
  return data?.user.subscription?.plan?.tier === SubscriptionPlanTier.Trial;
}

// todo: at some point we may want to register business trial here

/**
 * @todo apply a more stable tier identity in the future when available
 * note this does not need to be handled at this time AFAIK
 */
export function useIsDesignerTrial() {
  const { data } = useUserContext();
  return (
    data?.user.roles.some(role => role.id === AUTH0_DESIGNER_BETA_ROLE_ID) ||
    data?.user.subscription?.plan?.tier === SubscriptionPlanTier.DesignerTrial
  );
}

/**
 * @todo apply a more stable tier identity in the future when available
 */
export function useIsProducerSubscription() {
  const { data } = useUserContext();
  return (
    data?.user.roles.some(role => role.id === AUTH0_PRODUCER_ROLE_ID) ||
    data?.user.subscription?.plan?.tier === SubscriptionPlanTier.Producer
  );
}

export function hasMakerRole(roles: Role[]) {
  return roles.some(role => role.id === AUTH0_MAKER_ROLE_ID);
}

/**
 * @todo apply a more stable tier identity in the future when available
 * @note when mixed teams are supported, this will need to be updated
 */
export function useIsMaker() {
  const { data } = useUserContext();
  return (
    (data?.user.roles && hasMakerRole(data.user.roles)) ||
    data?.user.subscription?.plan?.tier === SubscriptionPlanTier.Maker
  );
}

/**
 * @todo apply a more stable tier identity in the future when available
 * @note when mixed teams are supported, this will need to be updated
 */
export function useIsCreative() {
  const { data } = useUserContext();
  return (
    data?.user.roles.some(role => role.id === AUTH0_CREATIVE_ROLE_ID) ||
    data?.user.subscription?.plan?.tier === SubscriptionPlanTier.Creative
  );
}

/**
 * @todo apply a more stable tier identity in the future when available
 * @note when mixed teams are supported, this will need to be updated
 */
export function useIsBusiness() {
  const { data } = useUserContext();
  return data?.user.subscription?.plan?.tier === SubscriptionPlanTier.Business;
}

export function hasEnterpriseRole(roles: Role[]) {
  return roles.some(role => role.id === AUTH0_TEAM_ENTERPRISE_ROLE_ID);
}

export function useIsEnterpriseUser() {
  const { data } = useUserContext();
  return (
    (data?.user.roles && hasEnterpriseRole(data.user.roles)) ||
    data?.user.subscription?.plan?.tier === SubscriptionPlanTier.Enterprise
  );
}

export function useIsEnterpriseOwnerOrAdmin() {
  const { data } = useUserContext();

  return (
    isTeamAdminOrOwner(data?.user.team) &&
    data?.user.subscription?.plan.tier === SubscriptionPlanTier.Enterprise
  );
}

export function useIsWellSaidAdmin() {
  const { data } = useUserContext();
  return data?.user.isAdmin || false;
}

export function useUserHasTeam() {
  return Boolean(useUserTeam());
}

export function useIsRbacEnabled() {
  const { data } = useUserContext();
  return data?.user.isRBACEnabled;
}

export const unpackAbilities = (userAbilities: string) => {
  const unpackedAbility = unpackAbility(userAbilities);
  return new Ability(unpackedAbility) as UserAbility;
};

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [churnkeyLoaded, setChurnkeyLoaded] = useState<boolean>(
    isBrowser && window.churnkey
  );
  const [appCuesLoaded, setAppCuesLoaded] = useState<boolean>(
    isBrowser && window.Appcues
  );
  const query = useUserContextQuery();
  const { data: userSubscription } = useUserSubscription();

  useEffect(() => {
    if (query.data && query.data.user) {
      setUserId(query.data.user.id);
    } else {
      setUserId(null);
    }
  }, [query.data]);

  useEffect(() => {
    setUserContextForSentry(userId);
  }, [userId]);

  const userAbilities = query.data?.user.abilities;
  const memoizedAbility = useMemo<UserAbility>(() => {
    try {
      if (!userAbilities) return new Ability();
      return unpackAbilities(userAbilities);
    } catch (error) {
      return new Ability();
    }
  }, [userAbilities]);

  const ability = memoizedAbility;

  useEffect(() => {
    const onChurnkeyLoad = () => {
      setChurnkeyLoaded(true);
    };
    const script = document.getElementById('churnkey-script-loader');
    script?.addEventListener('load', onChurnkeyLoad);
    return () => {
      script?.removeEventListener('load', onChurnkeyLoad);
    };
  }, []);

  useEffect(() => {
    /**
     * Checks the status of the Stripe subscription and if it's paused,
     * a pause wall (handled by Churnkey) will be presented to the user.
     * @see https://docs.churnkey.co/pause-wall
     */
    if (
      churnkeyLoaded &&
      ability.can(Actions.UseFeature, Subjects.Churnkey) &&
      userSubscription?.user.subscription
    ) {
      checkSubscriptionPause({
        subscription: userSubscription?.user.subscription,
        handleLogout: () => {
          createSigoutProps().onClick();
        },
      });
    }
  }, [ability, churnkeyLoaded, userSubscription?.user.subscription]);

  useEffect(() => {
    const onAppCuesLoaded = () => {
      setAppCuesLoaded(true);
    };
    const script = document.getElementById('appcues-script-loader');
    script?.addEventListener('load', onAppCuesLoaded);
    return () => {
      script?.removeEventListener('load', onAppCuesLoaded);
    };
  }, [setAppCuesLoaded]);

  useEffect(() => {
    if (environment !== 'testing' && appCuesLoaded) {
      window.Appcues.identify(userId, {
        email: query.data?.user.profile.email,
        env: environment,
        created_at: query.data?.user.profile.createdAt,
        given_name: query.data?.user.profile.givenName,
        family_name: query.data?.user.profile.familyName,
        company: query.data?.user.profile.company,
        company_name: query.data?.user.team?.companyName,
        is_managed_team: query.data?.user.team?.managed,
        subscription_plan_id: query.data?.user.subscription?.plan.id,
        subscription_plan_display_name:
          query.data?.user.subscription?.plan.displayName,
        tier: query.data?.user.subscription?.plan.tier,
        tier_interval: query.data?.user.subscription?.plan.interval,
        is_trial: query.data?.user.subscription?.plan.isTrial,
        is_team: query.data?.user.subscription?.plan.isMultiSeat,
        role_ids: query.data?.user.roles.map(r => r.id).join(','),
        role_names: query.data?.user.roles.map(r => r.name).join(','),
        team_role: query.data?.user.team?.viewersRoleOnTeam,
      });
    }
  }, [
    appCuesLoaded,
    query.data?.user.profile.company,
    query.data?.user.profile.createdAt,
    query.data?.user.profile.email,
    query.data?.user.profile.familyName,
    query.data?.user.profile.givenName,
    query.data?.user.roles,
    query.data?.user.subscription?.plan.displayName,
    query.data?.user.subscription?.plan.id,
    query.data?.user.subscription?.plan.interval,
    query.data?.user.subscription?.plan.isMultiSeat,
    query.data?.user.subscription?.plan.isTrial,
    query.data?.user.subscription?.plan.tier,
    query.data?.user.team?.companyName,
    query.data?.user.team?.managed,
    userId,
    query.data?.user.team?.viewersRoleOnTeam,
  ]);

  return (
    <UserContext.Provider value={query}>
      <UserAbilitiesContext.Provider value={memoizedAbility}>
        {children}
      </UserAbilitiesContext.Provider>
    </UserContext.Provider>
  );
};
